// Supabase service for HKDSE Quiz Data
// Replaces hkdseQuizData.js and hkdseQuizDataTypeA-F.js imports

import { querySupabase } from '../supabaseClient';

/**
 * Get quiz by text ID
 */
export const getHKDSEQuizByTextId = async (textId) => {
  try {
    const records = await querySupabase('chi_quizdata', { text_id: textId }, {
      order_by: 'question_id',
      order_direction: 'asc'
    });
    
    if (!records || records.length === 0) return null;
    
    // Transform Supabase records to component-friendly format
    const firstRecord = records[0];
    const questions = records.map(record => ({
      id: record.question_id,
      question: record.question_text,
      type: record.question_type,
      options: [
        record.option_a,
        record.option_b,
        record.option_c,
        record.option_d
      ],
      correct: record.correct_answer,
      explanation: record.explanation,
      points: record.points
    }));
    
    return {
      id: firstRecord.quiz_id,
      textId: firstRecord.text_id,
      title: firstRecord.title,
      author: firstRecord.author,
      dynasty: firstRecord.dynasty,
      category: firstRecord.category,
      difficulty: firstRecord.difficulty,
      timeLimit: firstRecord.time_limit,
      questions
    };
  } catch (error) {
    console.error('Error fetching quiz data:', error);
    return null;
  }
};

/**
 * Get quizzes by category
 */
export const getHKDSEQuizzesByCategory = async (category) => {
  try {
    const records = await querySupabase('chi_quizdata', { category }, {
      order_by: 'text_id',
      order_direction: 'asc'
    });
    
    if (!records || records.length === 0) return [];
    
    // Group by quiz_id
    const quizMap = {};
    records.forEach(record => {
      if (!quizMap[record.quiz_id]) {
        quizMap[record.quiz_id] = {
          id: record.quiz_id,
          textId: record.text_id,
          title: record.title,
          questions: []
        };
      }
      quizMap[record.quiz_id].questions.push({
        id: record.question_id,
        question: record.question_text,
        type: record.question_type,
        options: [record.option_a, record.option_b, record.option_c, record.option_d],
        correct: record.correct_answer,
        explanation: record.explanation,
        points: record.points
      });
    });
    
    return Object.values(quizMap);
  } catch (error) {
    console.error('Error fetching quizzes by category:', error);
    return [];
  }
};

/**
 * Get quizzes by difficulty
 */
export const getHKDSEQuizzesByDifficulty = async (difficulty) => {
  try {
    const records = await querySupabase('chi_quizdata', { difficulty }, {
      order_by: 'text_id',
      order_direction: 'asc'
    });
    
    if (!records || records.length === 0) return [];
    
    // Group by quiz_id (same transformation as category)
    const quizMap = {};
    records.forEach(record => {
      if (!quizMap[record.quiz_id]) {
        quizMap[record.quiz_id] = {
          id: record.quiz_id,
          textId: record.text_id,
          title: record.title,
          questions: []
        };
      }
      quizMap[record.quiz_id].questions.push({
        id: record.question_id,
        question: record.question_text,
        type: record.question_type,
        options: [record.option_a, record.option_b, record.option_c, record.option_d],
        correct: record.correct_answer,
        explanation: record.explanation,
        points: record.points
      });
    });
    
    return Object.values(quizMap);
  } catch (error) {
    console.error('Error fetching quizzes by difficulty:', error);
    return [];
  }
};

/**
 * Get grade level based on score
 */
export const getHKDSEGradeLevel = (score) => {
  if (score >= 90) {
    return { label: '優秀', bgColor: 'bg-green-100', color: 'text-green-800' };
  } else if (score >= 80) {
    return { label: '良好', bgColor: 'bg-blue-100', color: 'text-blue-800' };
  } else if (score >= 70) {
    return { label: '中等', bgColor: 'bg-yellow-100', color: 'text-yellow-800' };
  } else if (score >= 60) {
    return { label: '及格', bgColor: 'bg-orange-100', color: 'text-orange-800' };
  } else {
    return { label: '需努力', bgColor: 'bg-red-100', color: 'text-red-800' };
  }
};

/**
 * Get study recommendations based on score and category
 */
export const getHKDSEStudyRecommendations = (score, category) => {
  const recommendations = [];
  
  if (score < 60) {
    recommendations.push('建議重新閱讀原文，加強對基礎概念的理解');
    recommendations.push('多做練習題，熟悉題型和答題技巧');
  } else if (score < 70) {
    recommendations.push('重點複習錯題，理解答題思路');
    recommendations.push('加強對文中論證手法和修辭技巧的掌握');
  } else if (score < 80) {
    recommendations.push('可以嘗試更高難度的題目');
    recommendations.push('深化對文本主題和思想內涵的理解');
  } else {
    recommendations.push('表現優秀！繼續保持學習熱情');
    recommendations.push('可以開始準備其他篇章的學習');
  }
  
  if (category) {
    recommendations.push(`針對${category}類型的文本需要特別注意相關技巧的運用`);
  }
  
  return recommendations;
};

// Type-specific quiz functions (for Type B, C, D, E, F)
export const getTypeBQuizByType = async (textId, categoryType) => {
  return getCategoryQuizByType(textId, categoryType, 'chi_quizdata');
};

export const getTypeCQuizByType = async (textId, categoryType) => {
  return getCategoryQuizByType(textId, categoryType, 'chi_quizdata');
};

export const getTypeDQuizByType = async (textId, categoryType) => {
  return getCategoryQuizByType(textId, categoryType, 'chi_quizdata');
};

export const getTypeEQuizByType = async (textId, categoryType) => {
  return getCategoryQuizByType(textId, categoryType, 'chi_quizdata');
};

export const getTypeFQuizByType = async (textId, categoryType) => {
  return getCategoryQuizByType(textId, categoryType, 'chi_quizdata');
};

// Internal helper for category quiz
const getCategoryQuizByType = async (textId, categoryType, table) => {
  try {
    const records = await querySupabase('chi_hkdsecategoryquiz', {
      text_id: textId,
      quiz_category_type: categoryType
    }, {
      order_by: 'question_id',
      order_direction: 'asc'
    });
    
    if (!records || records.length === 0) return null;
    
    const firstRecord = records[0];
    const questions = records.map(record => ({
      id: record.question_id,
      question: record.question_text,
      type: record.question_type,
      options: record.question_options || [],
      correct: record.question_correct_answer,
      explanation: record.question_explanation,
      points: record.question_points
    }));
    
    return {
      id: firstRecord.quiz_id,
      title: firstRecord.quiz_title,
      description: firstRecord.quiz_description,
      category: firstRecord.quiz_category,
      difficulty: firstRecord.quiz_difficulty,
      timeLimit: firstRecord.quiz_time_limit,
      questions
    };
  } catch (error) {
    console.error('Error fetching category quiz:', error);
    return null;
  }
};

